import React, { useCallback, useRef, useState } from "react";
import { User as UserType, UserPayout } from "@/src/types/user";
import { useHistory } from "react-router-dom";
import useSearchParams from "@/src/hooks/useSearchParams";
import { useInputDialog } from "@/src/components/inputDialog";
import { Button } from "@/src/components/ui/button";
import { Input } from "@/src/components/ui/input";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/src/components/ui/card";
import { useToast } from "@/src/hooks/use-toast";
import {
  fetchUser,
  persistPayout,
  resendEmail,
  verifyLiveness,
} from "@/src/lib/api";
import { UserDetails } from "./UserDetails";
import { PayoutTable } from "./PayoutTable";
import { FraudTable } from "./FraudTable";
import { SocialMediaPostTable } from "./SocialMediaPostTable";
import AddCoinsModal, { AddCoinsModalRef } from "./AddCoinsModal";
import { Skeleton } from "@/src/components/ui/skeleton";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import RewardHistoryModal, {
  RewardHistoryModalRef,
} from "@/src/views/admin/user/RewardHistoryModal";

function User() {
  const history = useHistory();
  const query = useSearchParams();
  const queryClient = useQueryClient();
  const paramUserId = query.get("userId");

  const { toast } = useToast();

  const userIDInputRef = useRef<HTMLInputElement>(null);
  const [userId, setUserId] = useState(paramUserId ?? "");
  const [loading, setLoading] = useState(false);

  const addCoinsModalRef = useRef<AddCoinsModalRef>(null);
  const userRewardHistoryModalRef = useRef<RewardHistoryModalRef>(null);

  const {
    openDialog: openPeristWithDifferentEmailDialog,
    InputDialog: PersistWithDifferentEmailDialog,
  } = useInputDialog({
    title: "Persist Payout",
    description:
      "Different email can be entered to persist this payout if needed. Leave as is to persist with the same email.",
  });

  const { data, isLoading } = useQuery<UserType>({
    enabled: !!userId,
    queryKey: ["user", userId],
    queryFn: () =>
      fetchUser(userId)
        .then((res) => {
          history.push(`/users?userId=${userId}`);
          return { ...res, userId: parseInt(userId) };
        })
        .catch((e) => {
          toast({
            title: "User not found",
            variant: "destructive",
          });
          return null;
        }),
  });

  const { mutate: verifyLivenessMutation, isPending: isVerifyingLiveness } =
    useMutation({
      mutationFn: (id: string) => verifyLiveness(id),
      onSuccess: () => {
        toast({
          title: "Liveness verified",
          variant: "default",
        });
        queryClient.invalidateQueries({
          queryKey: ["user", userId],
        });
      },
      onError: () => {
        toast({
          title: "Failed to verify liveness",
          variant: "destructive",
        });
      },
    });

  const handleVerifyLiveness = useCallback(() => {
    verifyLivenessMutation(userId);
  }, [userId, verifyLivenessMutation]);

  const handlePersistPayout = useCallback(
    async (_payout: UserPayout, isNewEmailEntered?: boolean) => {
      try {
        setLoading(true);
        await persistPayout(
          _payout.payoutId,
          isNewEmailEntered ? _payout.mail : null,
        );
        toast({
          title: "Payout persisted",
          variant: "default",
        });
        queryClient.invalidateQueries({
          queryKey: ["user", userId],
        });
      } catch (error) {
        console.error("Error on persisting user payout: ", error);
        toast({
          title: "Failed to persist payout",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    },
    [toast, queryClient, userId],
  );

  const handleResendEmail = useCallback(
    async (_payout: UserPayout) => {
      try {
        setLoading(true);
        await resendEmail(_payout.payoutId);
        toast({
          title: "Mail sent!",
          variant: "default",
        });
        queryClient.invalidateQueries({
          queryKey: ["user", userId],
        });
      } catch (error) {
        console.error("Error on resending email: ", error);
        toast({
          title: "Failed to send mail.",
          variant: "destructive",
        });
      } finally {
      }
    },
    [toast, queryClient, userId],
  );

  const onPersistWithDifferentEmail = useCallback(
    async (payout: UserPayout) => {
      const result = await openPeristWithDifferentEmailDialog(payout.mail);
      const isCanceled = result === false;

      if (isCanceled) return;

      const isNewEmail = result !== payout.mail;
      const tempPayout = isNewEmail ? { ...payout, mail: result } : payout;
      handlePersistPayout(tempPayout, isNewEmail);
    },
    [openPeristWithDifferentEmailDialog, handlePersistPayout],
  );

  return (
    <div className="container mx-auto p-4">
      {PersistWithDifferentEmailDialog}
      <Card>
        <CardHeader>
          <CardTitle>User Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex space-x-2 mb-4">
            <Input
              ref={userIDInputRef}
              defaultValue={paramUserId ?? ""}
              placeholder="Enter User ID"
              onKeyDown={(e) => {
                if (e.key === "Enter" && e.currentTarget.value === "") {
                  toast({
                    title: "Please enter a user ID",
                    variant: "default",
                  });
                }
                if (e.key === "Enter") {
                  setUserId(e.currentTarget.value);
                }
              }}
            />
            <Button
              onClick={() => setUserId(userIDInputRef.current.value)}
              disabled={isLoading}
            >
              {loading ? "Loading..." : "Get User"}
            </Button>
          </div>

          {isLoading ? (
            <>
              <UserDetailsSkeleton />
              <PayoutTableSkeleton />
              <FraudTableSkeleton />
              <SocialMediaPostTableSkeleton />
            </>
          ) : data ? (
            <>
              <UserDetails
                onAddCoinsClick={() => {
                  addCoinsModalRef.current?.openModal();
                }}
                onRewardHistoryClick={() =>
                  userRewardHistoryModalRef.current?.openModal()
                }
                onVerifyLivenessClick={handleVerifyLiveness}
                isVerifyingLiveness={isVerifyingLiveness}
                user={data}
              />
              <AddCoinsModal
                ref={addCoinsModalRef}
                userId={data.userId.toString()}
                installedGames={data.userInstalledGames ?? []}
              />
              <RewardHistoryModal
                ref={userRewardHistoryModalRef}
                userID={data.userId.toString()}
                installedGames={data.userInstalledGames ?? []}
                userTotalCoinsPerGame={data.userTotalCoinsPerGame}
              />
              <PayoutTable
                payouts={data.userPayouts ?? []}
                onPersistPayout={onPersistWithDifferentEmail}
                onResendEmail={handleResendEmail}
                isPersistingBlocked={data.isPersistingBlocked}
              />
              <FraudTable frauds={data.userFraudReasons ?? []} />
              <SocialMediaPostTable posts={data.socialMediaPost ?? []} />
            </>
          ) : (
            <div className="p-4 bg-gray-50">
              <h2 className={clsx("text-center text-md font-medium")}>
                {userId ? "User not found" : "Enter a user ID to get started"}
              </h2>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default User;

function UserDetailsSkeleton() {
  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle>User Information</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4">
          {[...Array(6)].map((_, index) => (
            <div key={index}>
              <Skeleton className="h-4 w-1/2 mb-2" />
              <Skeleton className="h-6 w-full" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function PayoutTableSkeleton() {
  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle>Payouts</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <Skeleton className="h-8 w-full" />
          {[...Array(5)].map((_, index) => (
            <Skeleton key={index} className="h-12 w-full" />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function FraudTableSkeleton() {
  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle>Fraud</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <Skeleton className="h-8 w-full" />
          {[...Array(3)].map((_, index) => (
            <Skeleton key={index} className="h-12 w-full" />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function SocialMediaPostTableSkeleton() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Social Media Posts</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <Skeleton className="h-8 w-full" />
          {[...Array(3)].map((_, index) => (
            <Skeleton key={index} className="h-12 w-full" />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
